/**
 * StructurePlanPanel — chat-driven proposal of the subject's outline
 * (sections → sub-sections → seed keywords). Lives on the Project
 * Settings "Plan structure" tab.
 *
 * VOCABULARY. The user reads "section" and "sub-section", matching the
 * outline rail in SubjectWorkspace. The rows, the API and the planner
 * agent still say Category and Topic — renaming those is a separate
 * job, so the two words meet here and nowhere else.
 *
 * Layout: working-tree preview on the left, chat on the right. Each
 * tree node has a delete button so the user can prune the LLM's
 * suggestions. "Accept & create" commits the tree as real
 * Category / Topic / TopicKeyword rows; existing names are skipped
 * server-side so this is safe to re-run.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { planRows, planTotals } from './planTree';
import api from '../services/api';
import { QuickPromptBar } from './QuickPromptBar';
import { useWebSocket } from '../contexts/WebSocketContext';
import StageModelsWidget from './common/StageModelsWidget';
import Internals from './common/Internals';
import { AgentErrorBanner, AgentStatusChip } from './common/AgentErrorBanner';
import ChatMarkdown from './common/ChatMarkdown';
import PlannerQuestionCard from './PlannerQuestionCard';
import PlannerProgress from './PlannerProgress';
import BuildTierToggle, { loadBuildTier, saveBuildTier, type BuildTier } from './BuildTierToggle';
import ContextUsageChip from './ContextUsageChip';
import type {
  StructurePlanResponse,
  StructurePlanTree,
  StructurePlanCategoryNode,
  StructurePlanTopicNode,
  StructurePlanSnapshotSummary,
  StructurePlanSnapshotDetail,
  WebSocketEvent,
  SiteBrief,
} from '../types';

interface Props {
  projectId: number;
  /** When the panel opens from a category page, hint the agent to focus
   *  on this category and pre-collapse the tree to show only it. Both
   *  fields must be present for the focus to take effect. */
  focusCategoryId?: number;
  focusCategoryName?: string;
  /** Called after Apply creates rows, so the page behind the drawer can
   *  reload. Without it the new sections only appeared once the drawer
   *  was closed. */
  onApplied?: () => void;
}

export default function StructurePlanPanel({
  projectId,
  focusCategoryId,
  focusCategoryName,
  onApplied,
}: Props) {
  const [plan, setPlan] = useState<StructurePlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [buildTier, setBuildTier] = useState<BuildTier>(loadBuildTier);
  const chooseTier = (tier: BuildTier) => {
    setBuildTier(tier);
    saveBuildTier(tier);
  };
  const [accepting, setAccepting] = useState(false);
  const [acceptResult, setAcceptResult] = useState<string | null>(null);
  const [endingSession, setEndingSession] = useState(false);
  const [resetting, setResetting] = useState(false);
  // Which window of static quick-prompts to show; the ↻ button rotates it.
  const [quickPromptPage, setQuickPromptPage] = useState(0);
  // Per-category collapse state. true = open (default). Keyed by index in
  // the tree.categories array — fine for v1 since deleting a category
  // re-keys everything below it; the user just re-opens what they want.
  // Point-in-time snapshots: list panel toggle, fetched list, currently
  // previewed snapshot detail (read-only tree replaces the working tree
  // in the left pane while set), and the "restoring" busy flag.
  const [showVersions, setShowVersions] = useState(false);
  const [snapshots, setSnapshots] = useState<
    StructurePlanSnapshotSummary[] | null
  >(null);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [previewSnap, setPreviewSnap] =
    useState<StructurePlanSnapshotDetail | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);
  const [restoring, setRestoring] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const { subscribe } = useWebSocket();

  const refresh = async () => {
    try {
      const next = await api.getStructurePlan(projectId, focusCategoryId);
      setPlan(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load plan');
    }
  };

  // Initial load. Opened from a node (focusCategoryName present), collapse the
  // OTHER top-level branches so the user lands on what they were just looking
  // at. Only initialized here — later refreshes (websocket, refresh()) leave
  // the user's expand/collapse choices alone.
  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .getStructurePlan(projectId, focusCategoryId)
      .then((p) => {
        if (!live) return;
        setPlan(p);
        if (focusCategoryName && p.working_tree?.categories?.length) {
          const roots = p.working_tree.categories;
          // Only override the all-open default when the focus actually matches
          // something — otherwise the user is faced with a collapsed tree and
          // no idea why.
          if (roots.some((c) => c.name === focusCategoryName)) {
            setCollapsed(
              Object.fromEntries(
                roots
                  .filter((c) => c.name !== focusCategoryName)
                  .map((c) => [c.name, true]),
              ),
            );
          }
        }
      })
      .catch((e) => {
        if (live) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [projectId, focusCategoryId, focusCategoryName]);

  // Auto-scroll the chat to the bottom whenever messages change.
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [plan?.messages?.length, plan?.status]);

  const refreshSnapshots = async () => {
    setSnapshotsLoading(true);
    try {
      const list = await api.listStructurePlanSnapshots(projectId, focusCategoryId);
      setSnapshots(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load versions');
    } finally {
      setSnapshotsLoading(false);
    }
  };

  // Live agent lifecycle — refresh when the LLM finishes its turn.
  // Also refresh the versions list if it's open, since a successful
  // turn may have just produced a new auto-snapshot. Filter by scope
  // so a project-level panel ignores events for category-scoped
  // sessions (and vice versa) — both can be open at the same time.
  useEffect(() => {
    const myScope = focusCategoryId ?? null;
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'structure_plan') return;
      if (event.project_id !== projectId) return;
      if ((event.category_id ?? null) !== myScope) return;
      // 'tree_updated' arrives per mutating tool batch mid-turn, so the
      // proposal fills in as the agent works instead of appearing all at once
      // when the round ends — which on a slow local model can be a minute of
      // apparently-frozen UI. Snapshots only settle at end-of-turn.
      if (event.status === 'tree_updated') {
        refresh();
        return;
      }
      if (event.status === 'replied' || event.status === 'failed') {
        refresh();
        if (showVersions) refreshSnapshots();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, focusCategoryId, subscribe, showVersions]);

  const toggleVersions = async () => {
    const next = !showVersions;
    setShowVersions(next);
    if (next && snapshots === null) {
      await refreshSnapshots();
    }
  };

  const previewSnapshot = async (snapshotId: number) => {
    if (previewSnap?.id === snapshotId) {
      // Clicking the currently-previewed snapshot exits preview.
      setPreviewSnap(null);
      return;
    }
    setPreviewLoadingId(snapshotId);
    try {
      const detail = await api.getStructurePlanSnapshot(
        projectId,
        snapshotId,
        focusCategoryId,
      );
      setPreviewSnap(detail);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load snapshot');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const restoreSnapshot = async () => {
    if (!previewSnap || restoring) return;
    if (
      !window.confirm(
        'Restore this version?\n\n' +
          'The current working tree is automatically saved as a new ' +
          "version first, so you can switch back if you change your mind.",
      )
    ) {
      return;
    }
    setRestoring(true);
    setError(null);
    try {
      const next = await api.restoreStructurePlanSnapshot(
        projectId,
        previewSnap.id,
        focusCategoryId,
      );
      setPlan(next);
      setPreviewSnap(null);
      await refreshSnapshots();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const rebuildSite = () => {
    const thorough = buildTier === 'thorough';
    if (
      !window.confirm(
        `Rebuild the whole site with the ${thorough ? 'thorough' : 'quick'} planner?\n\n` +
          '• It builds the site again from your answers and the research room. ' +
          (thorough ? 'It takes about 10 to 20 minutes.\n' : 'It takes several minutes.\n') +
          '• The current draft is saved under Versions, so you can go back to it.',
      )
    ) {
      return;
    }
    void sendMessage('Rebuild the whole site from the brief and the room.', {
      rebuild: true,
    });
  };

  const [clearingBrief, setClearingBrief] = useState(false);
  const changeBriefAnswers = async () => {
    if (
      !window.confirm(
        'Change who this site is for?\n\n' +
          '• The saved answers are cleared, and your next message starts the questions again.\n' +
          '• The draft and the chat are kept.',
      )
    ) {
      return;
    }
    setClearingBrief(true);
    try {
      setPlan(await api.clearSiteBrief(projectId, focusCategoryId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not clear the answers');
    } finally {
      setClearingBrief(false);
    }
  };

  const sendMessage = async (
    overrideText?: string,
    opts?: { rebuild?: boolean },
  ) => {
    // Quick-prompt chips pass their text directly; the textarea path
    // falls back to the current draft. Every message goes to the planner
    // the toggle names.
    const text = (overrideText ?? chatInput).trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const next = await api.sendStructurePlanMessage(
        projectId,
        text,
        focusCategoryId,
        { ...opts, tier: buildTier },
      );
      setPlan(next);
      setChatInput('');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const deleteNode = async (path: (string | number)[]) => {
    try {
      const next = await api.deleteStructurePlanNode(
        projectId,
        path,
        focusCategoryId,
      );
      setPlan(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const acceptPlan = async () => {
    if (
      !window.confirm(
        'Apply this proposal now?\n\n' +
          '• New sections, sub-sections, and seed keywords are CREATED.\n' +
          '• Existing names are REUSED - new sub-sections/seeds merge into them.\n' +
          '• Nothing is renamed, deleted, or overwritten.\n\n' +
          'Safe to re-run.',
      )
    ) {
      return;
    }
    setAccepting(true);
    setAcceptResult(null);
    try {
      const result = await api.acceptStructurePlan(projectId, focusCategoryId);
      const made: string[] = [];
      if (result.categories_created)
        made.push(
          `${result.categories_created} section${result.categories_created === 1 ? '' : 's'}`,
        );
      if (result.topics_created)
        made.push(
          `${result.topics_created} sub-section${result.topics_created === 1 ? '' : 's'}`,
        );
      if (result.seed_keywords_created)
        made.push(
          `${result.seed_keywords_created} seed keyword${result.seed_keywords_created === 1 ? '' : 's'}`,
        );
      const head = made.length
        ? `Created ${made.join(', ')}.`
        : 'Nothing new to create - every node already existed.';
      const merged = result.skipped.length
        ? ` ${result.skipped.length} item${result.skipped.length === 1 ? '' : 's'} already existed and were merged into / left alone.`
        : '';
      setAcceptResult(head + merged);
      onApplied?.();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Accept failed');
    } finally {
      setAccepting(false);
    }
  };

  const startFreshChat = async () => {
    if (endingSession) return;
    if (
      !window.confirm(
        'Start a new chat?\n\n' +
          '• The current chat will be hidden from the LLM and from this view.\n' +
          '• It stays saved in the database (no data is lost).\n' +
          '• The proposed structure on the left is preserved - you can keep iterating on it.',
      )
    ) {
      return;
    }
    setEndingSession(true);
    setError(null);
    try {
      const next = await api.endStructurePlanSession(projectId, focusCategoryId);
      setPlan(next);
      setAcceptResult(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start fresh chat');
    } finally {
      setEndingSession(false);
    }
  };

  const resetPlan = async () => {
    if (resetting) return;
    if (
      !window.confirm(
        'Discard this draft?\n\n' +
          '• Chat history is wiped.\n' +
          "• The working tree is re-seeded from the subject's CURRENT " +
          'outline - sections, sub-sections and seed keywords - so you ' +
          'can edit / extend them.\n\n' +
          'Nothing in the subject itself is deleted.',
      )
    ) {
      return;
    }
    setResetting(true);
    setError(null);
    try {
      const next = await api.resetStructurePlan(projectId, focusCategoryId);
      setPlan(next);
      setAcceptResult(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const workingTree: StructurePlanTree | null = plan?.working_tree ?? null;
  // The tree the left pane actually displays — either a previewed
  // snapshot (read-only) or the live working tree.
  const tree: StructurePlanTree | null = previewSnap?.tree ?? workingTree;
  const isPreviewing = previewSnap !== null;
  // Counted over the WHOLE tree, not the top two levels — a proposal can be as
  // deep as the subject needs now, and a count that stopped at topics would
  // under-report everything below.
  const rows = useMemo(() => planRows(tree), [tree]);
  const totals = useMemo(() => planTotals(tree), [tree]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // A row is hidden when ANY ancestor is collapsed, which is a prefix test on
  // the path — cheaper and less stateful than tracking visibility per row.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          !Object.entries(collapsed).some(
            ([path, isShut]) => isShut && r.path.startsWith(`${path} / `),
          ),
      ),
    [rows, collapsed],
  );

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading planner…</div>;
  }
  if (error && !plan) {
    return (
      <div className="p-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
        {error}
      </div>
    );
  }
  if (!plan) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Plan site structure
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          The planner first asks who the site is for, then builds the
          sections, sub-sections and seed keywords for that reader. Delete
          anything you don't want, then click <em>Apply to subject</em>. New
          rows are created; names that already exist are reused (additions
          merge in). Nothing is renamed or deleted - safe to re-run.
        </p>
      </div>

      {error && (
        <div className="p-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
          {error}
        </div>
      )}
      {acceptResult && (
        <div className="p-2 text-sm text-green-800 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded">
          {acceptResult}
        </div>
      )}

      {plan.site_brief && (
        <SiteBriefBar
          brief={plan.site_brief}
          busy={clearingBrief || sending || plan.status === 'thinking'}
          onChange={changeBriefAnswers}
          onRebuild={(workingTree?.categories ?? []).length > 0 ? rebuildSite : undefined}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Tree pane */}
        <section className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 flex flex-col">
          {/* flex-wrap + whitespace-nowrap: in the drawer's narrow column the
              buttons used to squeeze, breaking "Apply to subject" over three
              lines. Now they move under the title as a row instead. */}
          <header className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {isPreviewing ? 'Previewing version' : 'Working proposal'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {totals.nodes} node{totals.nodes === 1 ? '' : 's'} ·{' '}
                {totals.seeds} seed{totals.seeds === 1 ? '' : 's'}
                {totals.depth > 1 && <> · {totals.depth} levels deep</>}
              </div>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              {isPreviewing ? (
                <>
                  <button
                    type="button"
                    onClick={restoreSnapshot}
                    disabled={restoring}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50"
                    title="Replace the current working tree with this snapshot. The current tree is auto-saved first so you can switch back."
                  >
                    {restoring ? 'Restoring…' : 'Restore this version'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewSnap(null)}
                    disabled={restoring}
                    className="px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Cancel preview
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={acceptPlan}
                    disabled={
                      accepting || !tree || tree.categories.length === 0
                    }
                    className="px-3 py-1 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50"
                    title="Create new nodes; reuse existing names (additions merge in). Nothing is renamed or deleted."
                  >
                    {accepting ? 'Applying…' : 'Apply to subject'}
                  </button>
                  <button
                    type="button"
                    onClick={toggleVersions}
                    className={`px-2 py-1 text-xs border rounded ${
                      showVersions
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                    title="Browse point-in-time snapshots saved before each AI revision. Click one to preview, then Restore to bring it back."
                  >
                    {`Versions${
                      plan.snapshot_count ? ` (${plan.snapshot_count})` : ''
                    }`}
                  </button>
                  <button
                    type="button"
                    onClick={resetPlan}
                    disabled={accepting}
                    className="px-2 py-1 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
                    title="Throw away this draft and its chat, and start again from the subject's current outline. Nothing in the subject is deleted."
                  >
                    Discard draft
                  </button>
                </>
              )}
            </div>
          </header>
          {isPreviewing && (
            <div className="px-3 py-2 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-xs text-amber-900 dark:text-amber-200 flex items-baseline gap-2">
              <svg
                className="w-3.5 h-3.5 mt-0.5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                Read-only preview of the version saved{' '}
                <strong>
                  {formatChatTime(previewSnap!.created_at)}
                </strong>
                {previewSnap!.kind === 'pre_restore' && (
                  <> (auto-saved before a previous restore)</>
                )}
                . Click <em>Restore this version</em> to swap it back in,
                or <em>Cancel preview</em> to return to the current draft.
              </span>
            </div>
          )}
          {showVersions && !isPreviewing && (
            <VersionsList
              snapshots={snapshots}
              loading={snapshotsLoading}
              loadingId={previewLoadingId}
              onSelect={previewSnapshot}
              onRefresh={refreshSnapshots}
              onClose={() => setShowVersions(false)}
            />
          )}
          {tree && tree.categories.length > 0 && (
            <div className="px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center gap-3 text-xs">
              <button
                type="button"
                onClick={() => setCollapsed({})}
                className="text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={() =>
                  setCollapsed(
                    Object.fromEntries(
                      rows.filter((r) => r.hasChildren).map((r) => [r.path, true]),
                    ),
                  )
                }
                className="text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Collapse all
              </button>
            </div>
          )}
          {/* Side by side, the panes stretch to the taller one (usually the
              chat), and a list capped at 60vh left a blank band under the
              last row. On wide screens the list fills the pane instead:
              positioned absolutely, it takes the pane's height without adding
              to it, and the min-height keeps it usable beside a short chat.
              Stacked (narrow), it keeps the 60vh cap. */}
          <div className="relative flex-1 lg:min-h-[60vh]">
          <div className="p-3 max-h-[60vh] overflow-y-auto lg:max-h-none lg:absolute lg:inset-0">
            {!tree || tree.categories.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                No proposal yet. Send a chat message to get started - e.g.
                "Lay out a structure for this site, focusing on beginner
                content."
              </div>
            ) : (
              /* One indented list, not a box per category. The boxes could
                 only ever show two levels because that is all the model had;
                 depth carries the structure now, the way the rail does it. */
              <ul className="text-sm">
                {visibleRows.map((row) => (
                  <li
                    key={row.path}
                    style={{ paddingLeft: row.depth * 18 }}
                    className="group flex items-start gap-1.5 py-1 border-l border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded"
                  >
                    {row.hasChildren ? (
                      <button
                        type="button"
                        onClick={() =>
                          setCollapsed((m) => ({ ...m, [row.path]: !m[row.path] }))
                        }
                        aria-label={collapsed[row.path] ? 'Expand' : 'Collapse'}
                        className="mt-0.5 w-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 leading-none"
                      >
                        {collapsed[row.path] ? '\u25B6' : '\u25BC'}
                      </button>
                    ) : (
                      /* The branch character, so a leaf reads as hanging off
                         its parent rather than floating at an indent. */
                      <span className="mt-0.5 w-4 text-gray-300 dark:text-gray-600 leading-none select-none">
                        {row.depth > 0 ? (row.isLast ? '\u2514' : '\u251C') : ''}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100 break-words">
                          {row.node.name}
                        </span>
                        {row.seedCount > 0 && (
                          <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {row.seedCount} seed{row.seedCount === 1 ? '' : 's'}
                          </span>
                        )}
                        {!isPreviewing && (
                          <button
                            type="button"
                            onClick={() => deleteNode(row.indexPath)}
                            title={`Remove ${row.path}`}
                            className="hidden group-hover:inline text-[11px] text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          >
                            remove
                          </button>
                        )}
                      </div>
                      {row.node.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                          {row.node.description}
                        </p>
                      )}
                      {row.seedCount > 0 && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 break-words">
                          {(row.node.seed_keywords ?? []).join(' · ')}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          </div>
        </section>

        {/* Chat pane */}
        <section className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 flex flex-col">
          <header className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Chat
              {plan.current_session_number > 1 && (
                <span
                  className="ml-2 text-[11px] font-normal text-gray-500 dark:text-gray-400"
                  title="Earlier sessions are kept, but this conversation starts fresh from here."
                >
                  · session {plan.current_session_number}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 whitespace-nowrap">
              <FreshChatButton
                disabled={
                  plan.status === 'thinking' ||
                  endingSession ||
                  plan.messages.length === 0
                }
                busy={endingSession}
                onClick={startFreshChat}
              />
              <ContextUsageChip
                contextTokens={plan.context_tokens}
                contextWindow={plan.context_window}
              />
              <StageModelsWidget surface="structure_planner" />
              {/* Only when it says something: "ready" on an idle chat was one
                  more chip in a crowded row. Working and failed still show. */}
              {plan.status !== 'idle' && (
                <AgentStatusChip status={plan.status} className="text-xs" />
              )}
            </div>
          </header>
          {plan.status === 'failed' && (
            <AgentErrorBanner error={plan.last_error} stage={plan.agent_stage} />
          )}
          <div className="flex-1 p-3 max-h-[55vh] overflow-y-auto space-y-2">
            {plan.messages.length === 0 ? (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                Ask the agent to propose a structure. Try something like "I
                run a small e-bike review site for trail riders. Suggest
                sections and sub-sections."
              </div>
            ) : (
              plan.messages
                // A build turn runs dozens of steps (keyword lookups, one
                // section at a time) with no text. Each drew a "(no content)"
                // bubble; the draft on the left shows that work as it happens.
                .filter(
                  (m) =>
                    !(
                      (m.entity_type || '').toUpperCase() === 'AI' &&
                      !m.content &&
                      m.has_tool_call
                    ),
                )
                .map((m) => <ChatBubble key={m.id} message={m} />)
            )}
            {plan.pending_questions && plan.status !== 'thinking' && (
              <PlannerQuestionCard
                card={plan.pending_questions}
                disabled={sending}
                onSubmit={(text) => sendMessage(text)}
              />
            )}
            {plan.status === 'thinking' && (
              <PlannerProgress
                workingSince={plan.working_since}
                tier={plan.working_tier}
                tree={workingTree}
                hasBrief={!!plan.site_brief}
              >
                <Internals>
                <button
                  type="button"
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Force-clear the "thinking" state? Use this if a previous request died (worker crashed). The page normally clears it by itself once the worker job has stopped.',
                      )
                    ) {
                      return;
                    }
                    try {
                      const next =
                        await api.clearStructurePlanThinking(
                          projectId,
                          focusCategoryId,
                        );
                      setPlan(next);
                    } catch (e) {
                      setError(
                        e instanceof Error
                          ? e.message
                          : 'Failed to clear thinking',
                      );
                    }
                  }}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline not-italic"
                  title="Force-flip the chat back to idle if it's stuck."
                >
                  Stuck? Clear it
                </button>
                </Internals>
              </PlannerProgress>
            )}
            <div ref={chatBottomRef} />
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 p-2">
            {/* Hidden while the question card waits: its answers are the
                next move, and chips beside it compete with them. */}
            {!plan.pending_questions && (
              <QuickPromptBar
                // The AI's chips answer its last reply, so an empty chat has
                // none to show - only the starters below.
                agentPrompts={plan.messages.length ? plan.suggested_prompts : null}
                staticPool={staticStructurePrompts(
                  plan.messages.length,
                  workingTree?.categories?.length ?? 0,
                  plan.room_name ?? null,
                  !!plan.site_brief,
                )}
                page={quickPromptPage}
                onCycle={() => setQuickPromptPage((p) => p + 1)}
                onSend={sendMessage}
                disabled={sending || plan.status === 'thinking'}
              />
            )}
            <textarea
              rows={3}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Tell the LLM what you want - e.g. 'add a section for accessories' or 'split Trail Bikes into beginner / intermediate'"
              disabled={sending || plan.status === 'thinking'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 dark:bg-gray-800"
            />
            <div className="flex items-center justify-between gap-2 mt-1.5">
              <BuildTierToggle
                value={buildTier}
                onChange={chooseTier}
                disabled={sending || plan.status === 'thinking'}
              />
              <button
                type="button"
                onClick={() => sendMessage()}
                disabled={
                  sending || plan.status === 'thinking' || !chatInput.trim()
                }
                className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {sending || plan.status === 'thinking' ? 'Working…' : 'Send'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------


// CategoryRow / TopicRow lived here: a box per category with a nested list of
// topics inside it, plus the seed list. They could only ever render two levels,
// because that is all the model had. The proposal draws as one indented branch
// now (see `planTree.ts`), so both are gone along with NodeDeleteButton, which
// only they used.
/** Who the planner is building for - the saved interview answers. */
function SiteBriefBar({
  brief,
  busy,
  onChange,
  onRebuild,
}: {
  brief: SiteBrief;
  busy: boolean;
  onChange: () => void;
  /** Shown once there is a draft: build it again with the chosen planner. */
  onRebuild?: () => void;
}) {
  const facts = [
    brief.site_role,
    brief.target_pages ? `about ${brief.target_pages} pages` : null,
    brief.exclusions ? `leaving out: ${brief.exclusions}` : null,
  ].filter(Boolean);
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-sm">
      <div className="min-w-0">
        <span className="font-medium text-gray-900 dark:text-gray-100">Planning for: </span>
        <span className="text-gray-700 dark:text-gray-300">{brief.audience}</span>
        {brief.goals && (
          <div className="text-xs text-gray-500 dark:text-gray-400">They come to: {brief.goals}</div>
        )}
        {facts.length > 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">{facts.join(' · ')}</div>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {onRebuild && (
          <button
            type="button"
            onClick={onRebuild}
            disabled={busy}
            title="Builds the whole site again from your answers and the room, with the planner chosen beside the chat (quick or thorough)."
            className="whitespace-nowrap px-2 py-1 text-xs rounded border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Rebuild site
          </button>
        )}
        <button
          type="button"
          onClick={onChange}
          disabled={busy}
          className="whitespace-nowrap px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Change answers
        </button>
      </div>
    </div>
  );
}

function FreshChatButton({
  disabled,
  busy,
  onClick,
}: {
  disabled?: boolean;
  busy?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="New chat: starts a fresh conversation. The draft on the left is kept; the old chat is hidden from the AI but stays saved."
      className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
    >
      {busy ? 'Starting…' : 'New chat'}
    </button>
  );
}




function VersionsList({
  snapshots,
  loading,
  loadingId,
  onSelect,
  onRefresh,
  onClose,
}: {
  snapshots: StructurePlanSnapshotSummary[] | null;
  loading: boolean;
  loadingId: number | null;
  onSelect: (snapshotId: number) => void;
  onRefresh: () => void;
  onClose: () => void;
}) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-indigo-50/40 dark:bg-indigo-900/30">
      <div className="px-3 py-1.5 flex items-center justify-between text-xs">
        <div className="font-medium text-indigo-900 dark:text-indigo-200">
          Earlier versions of this tree
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="text-indigo-700 dark:text-indigo-300 hover:underline disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 dark:text-gray-300 hover:underline"
          >
            Close
          </button>
        </div>
      </div>
      <div className="px-3 pb-2 text-[11px] text-indigo-800/80 dark:text-indigo-300">
        Saved automatically before each AI revision. Click one to preview;
        you can then restore it.
      </div>
      {loading && snapshots === null ? (
        <div className="px-3 pb-2 text-xs text-gray-500 dark:text-gray-400 italic">
          Loading versions…
        </div>
      ) : !snapshots || snapshots.length === 0 ? (
        <div className="px-3 pb-2 text-xs text-gray-500 dark:text-gray-400 italic">
          No earlier versions yet - they appear once the agent revises the
          tree at least once.
        </div>
      ) : (
        <ul className="divide-y divide-indigo-100 max-h-48 overflow-y-auto">
          {snapshots.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                disabled={loadingId === s.id}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-100 dark:hover:bg-indigo-900/40 disabled:opacity-60 flex items-center justify-between gap-2"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {formatChatTime(s.created_at)}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      s.kind === 'pre_restore' || s.kind === 'pre_rebuild'
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                    title={
                      s.kind === 'pre_restore'
                        ? 'Auto-saved before a previous restore'
                        : s.kind === 'pre_rebuild'
                          ? 'The draft as it was before a rebuild'
                          : 'Auto-saved before an AI revision'
                    }
                  >
                    {s.kind === 'pre_restore'
                      ? 'pre-restore'
                      : s.kind === 'pre_rebuild'
                        ? 'before rebuild'
                        : 'auto'}
                  </span>
                </span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400 shrink-0">
                  {s.category_count} section · {s.topic_count} sub ·{' '}
                  {s.seed_count} seed
                  {loadingId === s.id ? ' · loading…' : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function staticStructurePrompts(
  messageCount: number,
  categoryCount: number,
  roomName: string | null,
  hasBrief: boolean,
): string[] {
  // A linked room is the best place to start, so its prompts go first: the
  // bar shows four at a time. The first names the room, so the button says
  // where the answer will come from.
  if (categoryCount > 0) {
    // There's a working tree — flesh it out / sharpen it.
    const fromRoom = roomName
      ? [
          `Check this outline against "${roomName}" - what's missing?`,
          'Add seed keywords from what buyers search for in the room',
        ]
      : [];
    return [
      ...fromRoom,
      'Add seed keywords to each sub-section',
      'Are any sections too broad? Split them up',
      "Suggest a section we're missing",
      'Add a few more sub-sections to the thin sections',
      'Are any sections overlapping? Merge them',
      'Rename anything that’s vague or generic',
      'Fill in descriptions for the bare sections',
    ];
  }
  // No outline yet - whether or not anything has been said.
  const fromRoom = roomName
    ? [
        `Draft an outline from what "${roomName}" found`,
        'Use the ways the market splits in the room as the sections',
        'What do buyers search for most, according to the room?',
      ]
    : [];
  // No brief yet: any message starts the interview, so the first starter
  // says that plainly.
  const interview = hasBrief
    ? []
    : ['Help me plan this site - ask me what you need to know'];
  return [
    ...interview,
    ...fromRoom,
    messageCount === 0
      ? "I'm not sure where to start - draft a full outline for this subject"
      : 'Draft a starter set of sections and sub-sections',
    'What top-level sections would a competitor in this space have?',
    'Propose 5 top-level sections with a few sub-sections each',
    'Suggest a beginner-to-advanced content structure',
    'What buyer-journey stages should we have sections for?',
    'Outline a structure aimed at search intent',
  ];
}

function formatChatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}


function ChatBubble({
  message,
}: {
  message: {
    entity_type: string;
    content: string;
    has_tool_call: boolean;
    created_at: string;
  };
}) {
  // Case-insensitive — backend uppercases now, but old running processes
  // (or any future stringifier change) might emit 'human' / 'ai' instead.
  const role = (message.entity_type || '').toUpperCase();

  // SYSTEM messages are compaction summaries — render as a full-width
  // amber band with a "compacted earlier turns" label so the user can
  // see exactly when compaction happened in the conversation.
  if (role === 'SYSTEM') {
    return (
      <div className="my-1.5 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 rounded p-2.5 text-xs">
        <div className="flex items-baseline gap-2 mb-1 font-medium text-amber-800 dark:text-amber-300">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h10" />
          </svg>
          <span>Compacted earlier conversation</span>
          <span className="text-amber-600 dark:text-amber-400 font-normal" title={new Date(message.created_at).toLocaleString()}>
            {formatChatTime(message.created_at)}
          </span>
        </div>
        <div className="whitespace-pre-wrap break-words text-amber-900 dark:text-amber-200 italic">
          {message.content || '(empty summary)'}
        </div>
      </div>
    );
  }

  const isHuman = role === 'HUMAN';
  // Strong contrast between speakers: user is solid indigo (right-aligned),
  // LLM is neutral light gray with a sparkle prefix (left-aligned).
  const bubbleClass = isHuman
    ? 'bg-indigo-600 text-white rounded-br-sm'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-sm';
  const labelClass = isHuman ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400';
  return (
    <div className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-lg p-2.5 text-sm shadow-sm ${bubbleClass}`}>
        <div className={`flex items-baseline gap-2 mb-1 text-[10px] font-medium ${labelClass}`}>
          <span>{isHuman ? 'You' : 'LLM'}</span>
          <span className="opacity-70" title={new Date(message.created_at).toLocaleString()}>
            {formatChatTime(message.created_at)}
          </span>
        </div>
        {/* The agent writes markdown, so render it. Raw, a reply naming your topic
            arrived as "**Age & Life Stage**" with the asterisks showing, and a
            bulleted list arrived as a run of hyphens. What YOU typed is left alone —
            it is not markdown, and asterisks in it are asterisks. */}
        {message.content ? (
          isHuman ? (
            <div className="whitespace-pre-wrap break-words">{message.content}</div>
          ) : (
            <ChatMarkdown text={message.content} />
          )
        ) : (
          <div className={`text-xs italic ${isHuman ? 'text-indigo-200' : 'text-gray-500 dark:text-gray-400'}`}>
            (no content)
          </div>
        )}
        {message.has_tool_call && !isHuman && (
          <div className="text-[10px] text-indigo-700 dark:text-indigo-300 italic mt-1.5">
            ↻ updated the proposal
          </div>
        )}
      </div>
    </div>
  );
}
